"use client";

import { useMemo } from "react";
import { generateAmortizationSchedule } from "@/utils/amortization";

/** default formatter – only used when parent doesn’t pass one */
const defaultCurrency = (n: number) => "$" + n.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,");

export interface MortgageAmortizationTableProps {
    loanAmount: number;
    annualRate: number;
    loanTerm: number;
    /** optional – lets the parent re‑use its own formatter */
    toCurrency?: (value: number) => string;
}

const MortgageAmortizationTable: React.FC<MortgageAmortizationTableProps> = ({
    loanAmount,
    annualRate,
    loanTerm,
    toCurrency,
}) => {
    const schedule = useMemo(
        () => generateAmortizationSchedule(loanAmount, annualRate, loanTerm),
        [loanAmount, annualRate, loanTerm],
    );

    /* pick the formatter the parent gave us, fallback to default */
    const fmt = toCurrency ?? defaultCurrency;

    return (
        <div className="overflow-x-auto mt-10 2xl:pb-2">
            <h3 className="text-xl font-bold mb-4 text-center text-[#212459]">Monthly Payment Schedule</h3>

            <table className="w-full table-auto border border-gray-300 border-collapse bg-white shadow">
                <thead className="bg-blue text-white">
                    <tr>
                        <th className="px-3 py-2 text-[15px] border border-gray-300">Month</th>
                        <th className="px-3 py-2 text-[15px] border border-gray-300">Payment</th>
                        <th className="px-3 py-2 text-[15px] border border-gray-300">Balance</th>
                        <th className="px-3 py-2 text-[15px] border border-gray-300">Principal</th>
                        <th className="px-3 py-2 text-[15px] border border-gray-300">Interest</th>
                        <th className="px-3 py-2 text-[15px] border border-gray-300">Cum. Intr.</th>
                    </tr>
                </thead>

                <tbody>
                    {schedule.map((row) => (
                        <tr key={row.month} className="odd:bg-[#f6f9ff] even:bg-white text-center">
                            <td className="px-2 py-1 text-[15px] border border-gray-300">{row.month}</td>
                            <td className="px-2 py-1 text-[15px] border border-gray-300">{fmt(row.payment)}</td>
                            <td className="px-2 py-1 text-[15px] border border-gray-300">{fmt(row.balance)}</td>
                            <td className="px-2 py-1 text-[15px] border border-gray-300">{fmt(row.principal)}</td>
                            <td className="px-2 py-1 text-[15px] border border-gray-300">{fmt(row.interest)}</td>
                            <td className="px-2 py-1 text-[15px] border border-gray-300">
                                {fmt(row.cumulativeInterest)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default MortgageAmortizationTable;
